/**
 * Оповещения о новых сообщениях.
 *
 * ── Что здесь решается ──────────────────────────────────────────────────────
 *
 * Менеджер работает с несколькими аккаунтами и десятками диалогов. Без
 * оповещения новое сообщение видно только в списке — то есть только если на
 * список смотрят. Для рабочего инструмента этого мало.
 *
 * ── Правила, по которым оповещение НЕ показывается ──────────────────────────
 *
 * 1. Своё исходящее. Оповещать об отправленном самим собой бессмысленно, а с
 *    общим аккаунтом ещё и вредно: сообщение коллеги придёт как «своё» —
 *    Telegram не различает, кто из менеджеров писал.
 * 2. Открытый чат при активном окне. Человек уже смотрит на переписку.
 * 3. Разрешение не выдано или пользователь оповещения выключил.
 *
 * ── Про маскирование (§7 ТЗ) ────────────────────────────────────────────────
 *
 * В заголовок идёт та же подпись аккаунта, что и в интерфейс, — она приходит
 * с сервера уже обезличенной для тех, кому не положено видеть identity.
 * Собирать заголовок из полей DTO напрямую нельзя: у маскированного аккаунта
 * их просто нет, и «undefined» в оповещении — меньшее из зол по сравнению с
 * настоящим именем, которое туда однажды подставят.
 */

import type { TelegramChatDto, TelegramMessageDto } from '@tw/shared-types';
import { messageSummary } from './message-summary.ts';
import { accountLabel } from '../components/accountLabel.js';

const ENABLED_KEY = 'bucor.notifications';

/** Длина превью. Целиком сообщение в оповещении не нужно и небезопасно. */
const PREVIEW_LIMIT = 120;

export type NotificationState = 'unsupported' | 'denied' | 'off' | 'on';

/** Поддерживает ли рантайм оповещения вообще. */
function supported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function notificationState(): NotificationState {
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return isEnabled() && Notification.permission === 'granted' ? 'on' : 'off';
}

export function isEnabled(): boolean {
  try {
    return window.localStorage.getItem(ENABLED_KEY) === 'on';
  } catch {
    return false;
  }
}

function setEnabled(value: boolean): void {
  try {
    window.localStorage.setItem(ENABLED_KEY, value ? 'on' : 'off');
  } catch {
    // Недоступное хранилище — не причина ломать экран. Оповещения просто
    // не переживут перезагрузку.
  }
}

/**
 * Включить или выключить оповещения.
 *
 * Разрешение запрашивается ТОЛЬКО отсюда, то есть по нажатию человека.
 * Запрос при загрузке страницы браузеры и без того часто игнорируют, а
 * пользователь, которого спросили не вовремя, жмёт «запретить» — и вернуть
 * это можно только через настройки браузера.
 */
export async function toggleNotifications(): Promise<NotificationState> {
  if (!supported()) return 'unsupported';

  if (isEnabled()) {
    setEnabled(false);
    return 'off';
  }

  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();

  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  setEnabled(true);
  return 'on';
}

export interface NotifyDecision {
  direction: 'INBOUND' | 'OUTBOUND';
  chatId: string;
  /** Идентификатор открытого чата или null. */
  openChatId: string | null;
  /** Заглушён ли чат этим сотрудником. */
  muted: boolean;
  /** Видно ли окно приложения прямо сейчас. */
  windowVisible: boolean;
  /** Включил ли пользователь оповещения. */
  enabled: boolean;
  permission: NotificationPermission;
}

/**
 * Показывать ли оповещение.
 *
 * Вынесено отдельной чистой функцией не ради стиля: правил здесь пять, и
 * ошибка в любом из них выглядит одинаково безобидно — «иногда не приходит»
 * или «приходит лишнее». Ни то, ни другое не поймать глазами, а с DOM внутри
 * это не покрыть тестом.
 */
export function shouldNotify(input: NotifyDecision): boolean {
  if (!input.enabled || input.permission !== 'granted') return false;

  /*
   * Беззвучный чат молчит.
   *
   * Проверка стоит ПЕРВОЙ среди правил о сообщении: если человек заглушил
   * переписку, всё остальное — направление, открытость, видимость окна —
   * значения уже не имеет.
   */
  if (input.muted) return false;

  /*
   * Исходящее не оповещает.
   *
   * Признак — direction, а не sentBy. Сообщение, отправленное из самого
   * Telegram мимо нашей системы, приходит с sentBy = null, оставаясь
   * исходящим: по sentBy оно выглядело бы как чужое входящее, и менеджер
   * получал бы оповещение о том, что сам же и написал с телефона.
   */
  if (input.direction === 'OUTBOUND') return false;

  /*
   * Открытый чат при видимом окне — человек уже читает.
   *
   * Оба условия обязательны. Открытый чат при свёрнутом окне оповещать надо:
   * «открыт» тут означает лишь «был выбран», а не «на него смотрят».
   */
  if (input.openChatId === input.chatId && input.windowVisible) return false;

  return true;
}

interface IncomingInput {
  chatId: string;
  message: TelegramMessageDto;
  /** Идентификатор открытого чата или null. */
  openChatId: string | null;
  /** Список чатов — из него берутся название, подпись аккаунта и беззвучность. */
  chats: readonly TelegramChatDto[];
}

/**
 * Показать оповещение о входящем, если оно уместно.
 *
 * Функция намеренно ничего не возвращает и никогда не бросает: она вызывается
 * из обработчика realtime, и сбой оповещения не должен уносить обработку
 * события — сообщение важнее уведомления о нём.
 */
export function notifyIncoming(input: IncomingInput): void {
  try {
    if (!supported()) return;

    const chat = input.chats.find((c) => c.id === input.chatId);

    const allowed = shouldNotify({
      direction: input.message.direction,
      chatId: input.chatId,
      openChatId: input.openChatId,
      muted: chat?.muted === true,
      windowVisible: document.visibilityState === 'visible',
      enabled: isEnabled(),
      permission: Notification.permission,
    });

    if (!allowed) return;

    const title = chat?.title ?? 'Новое сообщение';
    const account = chat ? accountLabel(chat.account) : null;

    const notification = new Notification(account ? `${title} · ${account}` : title, {
      body: preview(input.message),
      // tag схлопывает поток сообщений из одного чата в одно оповещение:
      // десять реплик подряд не должны давать десять всплывающих окон.
      tag: `chat:${input.chatId}`,
      silent: false,
    });

    // Клик по оповещению возвращает к окну. Открыть нужный чат отсюда нельзя:
    // модуль не знает про состояние воркспейса и знать не должен.
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // Ровно то, о чём сказано выше.
  }
}

function preview(message: TelegramMessageDto): string {
  const summary = messageSummary(message);
  return summary.length > PREVIEW_LIMIT ? `${summary.slice(0, PREVIEW_LIMIT)}…` : summary;
}
